if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.petPersonalization = null;
        this.petPersonalizationControls = null;
        this.petPhotoInputs = [];
        this.petPhotoInputListeners = new WeakSet();
        this.petExampleToggleListeners = new WeakSet();
        this.petPhotoObjectUrls = new Map();
        this.petUploadOverlay = null;
        this.petUploadTitle = null;
        this.petUploadHelp = null;
        this.isPetBusy = false;
        this.petBusyElements = [];
        this.initializePetControls();
        queueMicrotask(() => this.initializePetControls());

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      getFormControls(selector) {
        return Array.from(this.form.elements).filter((control) => control.matches(selector));
      }

      initializePetControls() {
        const personalizationId = this.dataset.petPersonalizationId;
        this.petPersonalization = personalizationId ? document.getElementById(personalizationId) : null;
        this.petPersonalizationControls = this.petPersonalization?.querySelector(
          '[data-pet-personalization-controls]'
        );
        this.petUploadOverlay =
          this.petPersonalization?.querySelector('[data-pet-upload-overlay]') ||
          this.querySelector('[data-pet-upload-overlay]');
        this.petUploadTitle = this.petUploadOverlay?.querySelector('[data-pet-upload-title]');
        this.petUploadHelp = this.petUploadOverlay?.querySelector('[data-pet-upload-help]');
        this.petPhotoInputs = this.getFormControls('input[type="file"][name="properties[Pet photo]"]');

        this.petPhotoInputs.forEach((input) => {
          if (this.petPhotoInputListeners.has(input)) return;
          input.addEventListener('change', () => this.updatePetPhotoPreview(input));
          this.petPhotoInputListeners.add(input);
        });

        const exampleToggleRoot = this.petPersonalization || this;
        exampleToggleRoot.querySelectorAll('[data-pet-example-toggle]').forEach((toggle) => {
          if (this.petExampleToggleListeners.has(toggle)) return;
          toggle.addEventListener('click', () => this.togglePetPhotoExample(toggle));
          this.petExampleToggleListeners.add(toggle);
        });
      }

      disconnectedCallback() {
        this.petPhotoObjectUrls.forEach((objectUrl) => URL.revokeObjectURL(objectUrl));
        this.petPhotoObjectUrls.clear();
      }

      isSupportedPetPhoto(file) {
        if (!file) return false;

        const supportedTypes = ['image/jpeg', 'image/png', 'image/webp'];
        const hasSupportedExtension = /\.(jpe?g|png|webp)$/i.test(file.name);
        return hasSupportedExtension && (!file.type || supportedTypes.includes(file.type));
      }

      clearPetPhotoPreview(input) {
        const card = input.closest('.pet-personalization__card');
        const previewWrapper = card?.querySelector('[data-pet-photo-preview-wrapper]');
        const preview = card?.querySelector('[data-pet-photo-preview]');
        const action = card?.querySelector('[data-pet-photo-action]');
        const statusId = input.getAttribute('aria-describedby')?.split(' ')[0];
        const status = statusId ? document.getElementById(statusId) : null;
        const objectUrl = this.petPhotoObjectUrls.get(input);

        if (objectUrl) URL.revokeObjectURL(objectUrl);
        this.petPhotoObjectUrls.delete(input);
        input.removeAttribute('data-pet-photo-ready');
        if (preview) {
          preview.removeAttribute('src');
          preview.onload = null;
          preview.onerror = null;
        }
        if (previewWrapper) previewWrapper.hidden = true;
        if (action) action.textContent = action.dataset.chooseText;
        if (status) {
          status.textContent = status.dataset.emptyText || '';
          status.classList.toggle('visually-hidden', status.dataset.visuallyHidden === 'true');
        }
      }

      setPetBusy(isBusy, title = '', help = '') {
        this.initializePetControls();
        if (!this.petUploadOverlay) return;

        if (isBusy && !this.isPetBusy) {
          const productInfo = this.closest('product-info');
          const elements = [
            this.form,
            this.petPersonalizationControls,
            productInfo?.querySelector('variant-selects'),
            ...(productInfo?.querySelectorAll('.product-add-on') || []),
          ].filter(Boolean);
          this.petBusyElements = Array.from(new Set(elements), (element) => ({
            element,
            wasInert: element.inert,
          }));
          this.petBusyElements.forEach(({ element }) => {
            element.inert = true;
          });
        } else if (!isBusy && this.isPetBusy) {
          this.petBusyElements.forEach(({ element, wasInert }) => {
            element.inert = wasInert;
          });
          this.petBusyElements = [];
        }

        this.isPetBusy = isBusy;
        if (isBusy) {
          this.setAttribute('aria-busy', 'true');
        } else {
          this.removeAttribute('aria-busy');
        }
        this.classList.toggle('is-pet-busy', isBusy);
        this.petPersonalization?.classList.toggle('is-pet-busy', isBusy);
        this.petUploadOverlay.hidden = !isBusy;

        if (this.petUploadTitle) this.petUploadTitle.textContent = isBusy ? title : '';
        if (this.petUploadHelp) {
          this.petUploadHelp.textContent = isBusy ? help : '';
          this.petUploadHelp.hidden = !isBusy || !help;
        }
      }

      togglePetPhotoExample(toggle) {
        const contentId = toggle.getAttribute('aria-controls');
        const content = contentId ? document.getElementById(contentId) : null;
        if (!content) return;

        const willOpen = toggle.getAttribute('aria-expanded') !== 'true';
        toggle.setAttribute('aria-expanded', String(willOpen));
        toggle.setAttribute('aria-label', willOpen ? toggle.dataset.hideLabel : toggle.dataset.showLabel);
        content.hidden = !willOpen;
      }

      updatePetPhotoPreview(input) {
        const selectedFile = input.files?.[0];
        if (!selectedFile) return;

        if (!this.isSupportedPetPhoto(selectedFile)) {
          input.value = '';
          this.clearPetPhotoPreview(input);
          this.handleErrorMessage(this.dataset.unsupportedPetPhotoMessage);
          input.focus();
          return;
        }

        const card = input.closest('.pet-personalization__card');
        const previewWrapper = card?.querySelector('[data-pet-photo-preview-wrapper]');
        const preview = card?.querySelector('[data-pet-photo-preview]');
        const action = card?.querySelector('[data-pet-photo-action]');
        const statusId = input.getAttribute('aria-describedby')?.split(' ')[0];
        const status = statusId ? document.getElementById(statusId) : null;
        const previousObjectUrl = this.petPhotoObjectUrls.get(input);
        const objectUrl = URL.createObjectURL(selectedFile);
        const hasLocalPreview = Boolean(preview && previewWrapper);

        if (previousObjectUrl) URL.revokeObjectURL(previousObjectUrl);
        this.petPhotoObjectUrls.set(input, objectUrl);
        if (status) {
          status.textContent = hasLocalPreview ? this.dataset.preparingPetPhotoMessage : selectedFile.name;
          status.classList.add('visually-hidden');
        }
        if (action) action.textContent = action.dataset.changeText;
        if (!hasLocalPreview) return;

        input.removeAttribute('data-pet-photo-ready');
        previewWrapper.hidden = true;
        this.setPetBusy(true, this.dataset.preparingPetPhotoMessage);

        preview.onload = () => {
          preview.width = preview.naturalWidth;
          preview.height = preview.naturalHeight;
          input.setAttribute('data-pet-photo-ready', 'true');
          previewWrapper.hidden = false;
          if (status) status.textContent = this.dataset.petPhotoReadyMessage;
          this.handleErrorMessage();
          preview.onload = null;
          preview.onerror = null;
          this.setPetBusy(false);
        };
        preview.onerror = () => {
          input.value = '';
          this.clearPetPhotoPreview(input);
          this.handleErrorMessage(this.dataset.unsupportedPetPhotoMessage);
          this.setPetBusy(false);
          input.focus();
        };
        preview.src = objectUrl;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.isPetBusy || this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.initializePetControls();
        const petPhotoInput = this.getFormControls('input[name="properties[Pet photo]"][required]')[0];
        if (petPhotoInput && petPhotoInput.files.length === 0) {
          this.handleErrorMessage(this.dataset.missingPetPhotoMessage);
          petPhotoInput.focus();
          return;
        }

        const photoInputs = this.getFormControls('input[type="file"][name="properties[Pet photo]"]');
        for (const photoInput of photoInputs) {
          const petPhoto = photoInput.files?.[0];
          if (petPhoto && !this.isSupportedPetPhoto(petPhoto)) {
            this.handleErrorMessage(this.dataset.unsupportedPetPhotoMessage);
            photoInput.focus();
            return;
          }
        }

        const petNameInput = this.getFormControls('input[name="properties[Pet name]"]')[0];
        if (petNameInput?.required && petNameInput.value.trim() === '') {
          this.handleErrorMessage(this.dataset.missingPetNameMessage);
          petNameInput.focus();
          return;
        }
        if (petNameInput && petNameInput.value.trim().length > petNameInput.maxLength) {
          this.handleErrorMessage(this.dataset.petNameLengthMessage);
          petNameInput.focus();
          return;
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        this.setPetBusy(
          true,
          this.dataset.uploadingPetPhotoMessage,
          this.dataset.uploadingPetPhotoHelp
        );
        const productInfo = this.closest('product-info');
        const addOnInputs = productInfo?.querySelectorAll('[data-add-on-variant-id]:checked') || [];
        const addOnItems = Array.from(addOnInputs, (input) => ({
          id: Number(input.dataset.addOnVariantId),
          quantity: 1,
        }));
        const addOnCartStorageKey = 'family-gift-home-add-on-items';
        const shouldAddAddOnsOnCartPage = addOnItems.length > 0 && !this.cart;
        if (shouldAddAddOnsOnCartPage) {
          sessionStorage.setItem(addOnCartStorageKey, JSON.stringify(addOnItems));
        }
        const cartSections = this.cart
          ? this.cart.getSectionsToRender().map((section) => section.id)
          : [];
        if (this.cart) {
          formData.append('sections', cartSections);
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        config.body = formData;
        const addToCartRequest = fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((mainProductResponse) => {
            if (mainProductResponse.status || addOnItems.length === 0 || !this.cart) return mainProductResponse;

            const addOnConfig = fetchConfig();
            addOnConfig.headers['X-Requested-With'] = 'XMLHttpRequest';
            addOnConfig.body = JSON.stringify({
              items: addOnItems,
              sections: cartSections,
              sections_url: window.location.pathname,
            });

            return fetch(`${routes.cart_add_url}`, addOnConfig)
              .then((response) => response.json())
              .then((addOnResponse) => {
                if (!addOnResponse.status) return addOnResponse;

                const error = new Error(addOnResponse.description || addOnResponse.message);
                error.primaryResponse = mainProductResponse;
                throw error;
              })
              .catch((error) => {
                error.primaryResponse = error.primaryResponse || mainProductResponse;
                throw error;
              });
          });

        addToCartRequest
          .then((response) => {
            if (response.status) {
              if (shouldAddAddOnsOnCartPage) sessionStorage.removeItem(addOnCartStorageKey);
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              const responseErrorMessage = response.description || response.message || window.cartStrings?.error;
              this.handleErrorMessage(responseErrorMessage);
              this.dispatchCartErrorEvent(responseErrorMessage, 'INVALID');
              linesUpdateDeferred?.reject(new Error(responseErrorMessage));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            if (shouldAddAddOnsOnCartPage) sessionStorage.removeItem(addOnCartStorageKey);
            console.error(e);
            if (e.primaryResponse) {
              const addOnErrorMessage = 'Your main product was added, but the selected add-on could not be added.';
              this.error = false;
              this.handleErrorMessage(addOnErrorMessage);
              this.dispatchCartErrorEvent(addOnErrorMessage, 'ADD_ON_FAILED');
              this.resolveCartLinesUpdate(linesUpdateDeferred);

              if (this.cart) {
                CartPerformance.measure("add:paint-updated-sections", () => {
                  this.cart.renderContents(e.primaryResponse);
                });
              }
              return;
            }

            const uploadErrorMessage = this.petUploadOverlay
              ? this.dataset.petPhotoUploadFailedMessage
              : e.message || 'Network error';
            this.error = false;
            this.handleErrorMessage(uploadErrorMessage);
            this.dispatchCartErrorEvent(uploadErrorMessage, 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.setPetBusy(false);
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
